<template>
  <tr class="versions-row">
    <td colspan="8" class="p-0">
      <div class="versions-container p-3 bg-light border-top">
        <!-- Header s tlačítkem pro novou verzi -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">Verze kalkulace {{ calculation.number }}</h6>
          <button
              class="btn btn-outline-success btn-sm"
              :class="toggleNewVersionButton ? 'disabled' : ''"
              @click="calculationVersionNew">
            <i class="bi-plus-circle-dotted"></i>
            <span class="ms-1">Nová verze</span>
          </button>
        </div>

        <!-- Loading indikátor -->
        <div v-if="loading" class="text-center py-3">
          <div class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Načítání...</span>
          </div>
        </div>

        <!-- Seznam verzí -->
        <div v-else-if="versions.length > 0" class="versions-list">
          <div
              v-for="version in versions"
              :key="version.number"
              class="version-item p-3 mb-2 border rounded"
              :class="version.order_confirmed ? 'bg-warning bg-opacity-25' : 'bg-white'">

            <!-- Hlavní informace o verzi -->
            <div class="row align-items-center">
              <div class="col-md-1">
                <div class="fw-bold">Verze {{ version.number }}</div>
                <small class="text-muted">{{ version.tag }}</small>
              </div>

              <div class="col-md-1 text-center">
                <span class="badge bg-secondary">{{ version.pdf_type || '-' }}</span>
              </div>

              <div class="col-md-4">
                <!-- Editovatelný popis -->
                <div class="d-flex align-items-center">
                  <span v-if="editingVersionId !== version.id" class="me-2">
                    {{ version.description || 'Bez popisu' }}
                  </span>
                  <input
                      v-else
                      type="text"
                      class="form-control form-control-sm me-2"
                      v-model="editingDescription"
                      @keyup.enter="saveDescription(version)"
                      @keyup.escape="cancelEdit"
                      :ref="el => { if (el) descriptionInputs[version.id] = el }">

                  <!-- Tlačítka pro editaci -->
                  <div v-if="editingVersionId === version.id" class="d-flex">
                    <button
                        @click="saveDescription(version)"
                        class="btn btn-sm btn-outline-success me-1"
                        title="Uložit změny">
                      <i class="bi-check"></i>
                    </button>
                    <button
                        @click="cancelEdit"
                        class="btn btn-sm btn-outline-secondary"
                        title="Zrušit editaci">
                      <i class="bi-x"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div class="col-md-2">
                <small class="text-muted">{{ version.created_at }}</small>
              </div>
              <div class="col-md-1">
                <div class="fw-bold">Provize</div>
                <small class="text-muted">{{ version.data.calculation_summary.commission_price || '0,00' }} Kč</small>
              </div>

              <div class="col-md-3">
                <!-- Akční tlačítka -->
                <div class="d-flex justify-content-end">
                  <button
                      @click="showPdfFile(version.pdf_file)"
                      class="btn btn-sm btn-outline-dark me-1"
                      title="Otevřít nabídku - PDF"
                      v-tooltip="">
                    <i class="bi-eye"></i>
                  </button>

                  <button
                      @click="openSettings(version.id)"
                      class="btn btn-sm btn-outline-secondary me-1"
                      title="Zobrazit nastavení parametrů verze"
                      v-tooltip="">
                    <i class="bi-gear"></i>
                  </button>

                  <button
                      :class="(editingVersionId !== version.id && !toggleNewVersionButton) ? '' : 'disabled'"
                      @click="startEdit(version)"
                      class="btn btn-sm btn-outline-primary me-1"
                      title="Upravit popis verze"
                      v-tooltip="">
                    <i class="bi-pencil"></i>
                  </button>

                  <OrderButton
                      v-if="version.pdf_type !== null"
                      :version="version"
                      :disabled="toggleNewVersionButton && !version.order_confirmed"
                      @new-order="newOrder"
                      @edit-order="newOrder"
                      @show-order="showPdfFile(version.order_pdf)"
                      @cancel-order="cancelOrder(version.id)"
                      @confirm-order="confirmOrder(version.id)"
                      @unconfirm-order="unconfirmOrder(version.id)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pokud nejsou žádné verze -->
        <div v-else class="text-center py-3 text-muted">
          <i class="bi-info-circle me-2"></i>
          Žádné verze nenalezeny
        </div>
      </div>
    </td>
  </tr>

  <!-- Modální okno pro nastavení verze -->
  <VersionSettingsModal
      v-model:is-visible="isSettingsModalVisible"
      :version-id="selectedVersionId"
  />
</template>

<script setup>
import {ref, reactive, computed, nextTick, defineProps, defineEmits, defineExpose} from 'vue'
import {useRouter} from 'vue-router'
import myApi from '@/api/api'
import OrderButton from '@/components/versions/OrderButton.vue'
import VersionSettingsModal from '@/components/versions/VersionSettingsModal.vue'
import {useStore} from 'vuex'
import {showPdfFile} from "@/common/functions";

const router = useRouter()
const store = useStore()
const props = defineProps({
  calculation: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['versionsLoaded', 'calculationUpdated', 'calculationConfirmationChanged'])
const versions = reactive([])
const loading = ref(false)
const orderButtonKey = ref(0)

// Stav pro modál nastavení
const isSettingsModalVisible = ref(false)
const selectedVersionId = ref(null)

// Proměnné pro editaci
const editingVersionId = ref(null)
const editingDescription = ref('')
const descriptionInputs = ref({})

const toggleNewVersionButton = computed(() => {
  if (versions && versions.length > 0) {
    return versions.some(obj => obj.order_confirmed === true)
  }
  return false
})

// Načtení verzí při prvním zobrazení
const loadVersions = async () => {
  if (versions.length === 0) {
    loading.value = true
    try {
      const newVersions = await myApi.listCalculationVersions(props.calculation.uid)
      versions.length = 0
      versions.push(...newVersions)
      emit('versionsLoaded', versions)
    } catch (error) {
      console.error('Chyba při načítání verzí:', error)
    } finally {
      loading.value = false
    }
  }
}

const reloadCalculationVersions = async () => {
  try {
    const newVersions = await myApi.listCalculationVersions(props.calculation.uid)
    versions.length = 0
    versions.push(...newVersions)
  } catch (error) {
    console.error('Chyba při přenačítání verzí:', error)
  }
}

function calculationVersionNew() {
  store.dispatch('calculation_list/setFocusedRow', props.calculation.uid)
  router.push({
    name: 'calculationCalculate',
    query: {cid: props.calculation.uid}
  })
}

// Funkce pro otevření modálu nastavení
function openSettings(versionId) {
  selectedVersionId.value = versionId
  isSettingsModalVisible.value = true
}

// Funkce pro editaci popisu
function startEdit(version) {
  editingVersionId.value = version.id
  editingDescription.value = version.description || ''

  nextTick(() => {
    const inputElement = descriptionInputs.value[version.id]
    if (inputElement) {
      inputElement.focus()
    }
  })
}

function cancelEdit() {
  editingVersionId.value = null
  editingDescription.value = ''
}

async function saveDescription(version) {
  try {
    await myApi.updateCalculationVersionDescription(version.id, editingDescription.value)
    await reloadCalculationVersions()
    cancelEdit()
  } catch (error) {
    console.error('Chyba při aktualizaci popisu:', error)
  }
}

// Order funkce
const newOrder = async (versionId) => {
  store.dispatch("calculation_list/setFocusedRow", props.calculation.uid)
  await router.push({name: 'newOrderView', query: {version: versionId}})
}

const cancelOrder = async (versionId) => {
  if (confirm("Opravdu si přejete objednávku smazat? Operace je nenávratná!")) {
    await myApi.orderDelete(versionId)
    await reloadCalculationVersions()
    orderButtonKey.value += 1
  }
}

const confirmOrder = async (versionId) => {
  if (confirm("Opravdu chcete potvrdit objednávku?")) {
    await myApi.orderConfirm(versionId)
    await reloadCalculationVersions()

    // Emitovat událost o změně potvrzení s uid kalkulace
    emit('calculationConfirmationChanged', props.calculation.uid, true)

    orderButtonKey.value += 1
  }
}

const unconfirmOrder = async (versionId) => {
  if (confirm("Opravdu si přejete zrušit potvrzení objednávky?")) {
    await myApi.orderUnconfirm(versionId)
    await reloadCalculationVersions()

    // Zkontrolovat, zda má kalkulace ještě nějaké potvrzené verze
    const hasConfirmedVersions = versions.some(version => version.order_confirmed === true)

    // Emitovat událost o změně potvrzení s uid kalkulace
    emit('calculationConfirmationChanged', props.calculation.uid, hasConfirmedVersions)

    orderButtonKey.value += 1
  }
}

// Expose funkci pro načtení verzí
defineExpose({
  loadVersions
})
</script>

<style scoped>
.versions-container {
  border-left: 4px solid #0d6efd;
}

.version-item {
  transition: all 0.2s ease;
}

.version-item:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-control-sm {
  font-size: 0.875rem;
}
</style>